import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Repeat, ShieldCheck, Loader2, CheckCircle2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AutopayManage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [consent, setConsent] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = () => axios.get(`${API}/public/autopay/${token}`).then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || "This link is not valid or has expired"));
  useEffect(() => { load(); }, [token]);

  useEffect(() => {
    if (params.get("updated")) {
      let tries = 0;
      const poll = async () => {
        try {
          const { data: r } = await axios.get(`${API}/public/autopay/${token}/status`);
          if (r.status === "active" && r.label) { setNotice(`Payment method updated: ${r.label}`); return load(); }
        } catch (e) { /* retry */ }
        if (tries++ < 8) setTimeout(poll, 2000);
      };
      poll();
    }
    const setupToken = params.get("approval_token_id");
    if (params.get("setup_token_return") && setupToken) {
      axios.post(`${API}/public/autopay/${token}/complete-paypal`, { setup_token_id: setupToken }).then(() => { setNotice("PayPal payment method updated"); load(); }).catch((e) => setNotice(e.response?.data?.detail || "PayPal update could not be completed"));
    }
  }, [params, token]);

  const revoke = async () => {
    setBusy("revoke");
    try {
      await axios.post(`${API}/public/autopay/${token}/revoke`);
      setNotice("Autopay has been cancelled. A confirmation email is on its way.");
      setConfirm(false);
      await load();
    } catch (e) {
      setNotice(e.response?.data?.detail || "Could not cancel autopay right now");
    } finally { setBusy(null); }
  };

  const update = async () => {
    if (!consent) return setNotice("Please accept the autopay terms to continue.");
    setBusy("update");
    try {
      const { data: r } = await axios.post(`${API}/public/autopay/${token}/update`, { origin_url: window.location.origin, consent_version: data.consent_version });
      window.location.href = r.redirect_url;
    } catch (e) {
      setNotice(e.response?.data?.detail || "Could not start the update right now");
      setBusy(null);
    }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6"><p className="text-slate-700 text-center" data-testid="autopay-manage-error">{error}</p></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-slate-100"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;

  const active = data.status === "active";
  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="autopay-manage-page">
      <div className="max-w-lg mx-auto space-y-4">
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <p className="text-xs uppercase tracking-wide text-slate-500">{data.company}</p>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 mt-1"><Repeat className="w-6 h-6 text-indigo-600" /> Manage autopay</h1>
          <p className="text-sm text-slate-600 mt-2">Hello {data.customer_name}. This page controls automatic payments for your recurring {data.series?.interval || ""} invoices.</p>
        </div>
        {notice && <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-900" data-testid="autopay-manage-notice">{notice}</div>}
        <div className={`rounded-2xl shadow-lg p-6 text-white ${active ? "bg-gradient-to-br from-indigo-700 to-blue-800" : "bg-gradient-to-br from-slate-600 to-slate-800"}`} data-testid="autopay-manage-status-card">
          <p className="text-xs uppercase tracking-wide opacity-80">Status</p>
          <p className="text-2xl font-bold flex items-center gap-2" data-testid="autopay-manage-status">{active ? <CheckCircle2 className="w-6 h-6" /> : <XCircle className="w-6 h-6" />}{active ? "Autopay is on" : data.status === "requires_action" ? "Needs your confirmation" : data.status === "failed" ? "Paused after a failed payment" : "Autopay is off"}</p>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div><p className="opacity-80 text-xs">Payment method</p><p className="font-semibold" data-testid="autopay-manage-label">{data.label || "—"}</p></div>
            <div><p className="opacity-80 text-xs">Next charge</p><p className="font-semibold" data-testid="autopay-manage-next">{active && data.next_charge_date ? data.next_charge_date : "—"}</p></div>
            <div><p className="opacity-80 text-xs">Charged</p><p className="font-semibold">{data.charge_timing === "on_due_date" ? "On each due date" : "When invoices are issued"}</p></div>
            <div><p className="opacity-80 text-xs">Consent given</p><p className="font-semibold">{(data.consent_accepted_at || "").slice(0, 10) || "—"}</p></div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 space-y-3">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-indigo-600" /> Update payment method</h2>
          <p className="text-xs text-slate-500">{data.provider === "stripe" ? "You will be taken to a secure Stripe page to enter a new card. Nothing is charged now." : "You will be taken to PayPal to approve a new billing agreement. Nothing is charged now."}</p>
          <label className="flex items-start gap-2 text-xs text-slate-700 rounded-lg bg-slate-50 border border-slate-200 p-3 cursor-pointer">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-indigo-700" data-testid="autopay-manage-consent" />
            <span>{data.consent_text}</span>
          </label>
          <button onClick={update} disabled={!!busy} className="w-full rounded-xl bg-indigo-700 hover:bg-indigo-800 disabled:opacity-60 text-white py-3 font-semibold" data-testid="autopay-manage-update-button">{busy === "update" ? "Redirecting…" : data.provider === "stripe" ? "Update card" : "Update PayPal account"}</button>
        </div>

        {active && (
          <div className="bg-white rounded-2xl shadow-lg p-6 space-y-3">
            <h2 className="font-semibold text-slate-800">Cancel autopay</h2>
            <p className="text-xs text-slate-500">Your saved payment method is removed from {data.company}. Future invoices will be emailed to you for manual payment.</p>
            {!confirm ? (
              <button onClick={() => setConfirm(true)} className="w-full rounded-xl border-2 border-red-300 text-red-700 hover:bg-red-50 py-3 font-semibold" data-testid="autopay-manage-revoke-button">Turn off autopay</button>
            ) : (
              <div className="flex gap-2">
                <button onClick={revoke} disabled={!!busy} className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white py-3 font-semibold" data-testid="autopay-manage-revoke-confirm">{busy === "revoke" ? "Cancelling…" : "Yes, turn it off"}</button>
                <button onClick={() => setConfirm(false)} className="flex-1 rounded-xl border border-slate-300 py-3 font-semibold text-slate-700" data-testid="autopay-manage-revoke-cancel">Keep autopay</button>
              </div>
            )}
          </div>
        )}
        <p className="text-center text-xs text-slate-400 pb-6">This link is personal and expires after 30 days. Need help? Contact {data.company}.</p>
      </div>
    </div>
  );
}
